(() => {
  if (customElements.get('customer-review-list')) return;
  const text = (tag, className, value) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (value != null) element.textContent = value;
    return element;
  };
  const count = value => Number.isSafeInteger(Number(value)) && Number(value) >= 0 ? Number(value) : 0;
  const hash = value => { let n = 2166136261; for (const char of value) n = Math.imul(n ^ char.charCodeAt(0), 16777619); return (n >>> 0).toString(36); };
  const stars = rating => {
    const node = text('span', 'cr-stars', '★'.repeat(rating) + '☆'.repeat(5 - rating));
    node.setAttribute('role', 'img');
    node.setAttribute('aria-label', `${rating} out of 5 stars`);
    return node;
  };
  customElements.define('customer-review-list', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.options = { signal: this.controller.signal };
      this.visibleCount = 10;
      this.voteMemory = new Map();
      this.filter = this.querySelector('[data-filter]');
      this.sort = this.querySelector('[data-sort]');
      this.list = this.querySelector('[data-list]');
      this.dialog = this.querySelector('[data-dialog]');
      this.form = this.querySelector('[data-form]');
      this.readReviews();
      this.addSampleReviews();
      this.renderSummary();
      [this.filter, this.sort].forEach(el => el.addEventListener('change', () => { this.visibleCount = 10; this.render(); }, this.options));
      this.addEventListener('click', event => this.onClick(event), this.options);
      this.addEventListener('shopify:block:select', event => {
        const groupId = event.detail?.blockId;
        this.filter.value = 'all';
        const index = this.filtered().findIndex(review => review.group === groupId);
        if (index >= 0) { this.visibleCount = Math.max(this.visibleCount, Math.ceil((index + 1) / 10) * 10); this.render(); }
      }, this.options);
      this.dialog.addEventListener('close', () => this.opener?.focus({ preventScroll: true }), this.options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, this.options);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        for (const name of ['name', 'content']) {
          const input = this.form.elements.namedItem(name);
          input.setCustomValidity(input.value.trim().length >= (name === 'content' ? 10 : 1) ? '' : 'Please complete this field.');
        }
        if (!this.form.reportValidity()) return;
        const status = this.querySelector('[data-submit-status]');
        // Front-end confirmation only. Connect persistence here before accepting live reviews.
        status.textContent = 'Thank you for your feedback!';
        status.hidden = false;
        this.form.dataset.complete = 'true';
        status.tabIndex = -1;
        status.focus({ preventScroll: true });
      }, this.options);
      this.form.addEventListener('input', event => {
        event.target.setCustomValidity?.('');
        this.querySelector('[data-submit-status]').hidden = true;
      }, this.options);
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.dialog?.close();
    }

    readReviews() {
      this.reviews = [];
      const warnings = [];
      const ids = new Set();
      [...this.querySelectorAll('[data-review-group]')].slice(0, 50).forEach(group => {
        let entries;
        try { entries = JSON.parse(group.value || '[]'); if (!Array.isArray(entries)) throw Error(); }
        catch { warnings.push(`Group ${group.dataset.groupId}: use a valid JSON array.`); return; }
        if (entries.length > 50) warnings.push(`Group ${group.dataset.groupId}: only the first 50 reviews are used.`);
        entries.slice(0, 50).forEach((entry, index) => {
          const rating = Number(entry?.rating);
          if (!entry || typeof entry.name !== 'string' || !entry.name.trim() || typeof entry.content !== 'string' || !entry.content.trim() || !Number.isInteger(rating) || rating < 1 || rating > 5) {
            warnings.push(`Group ${group.dataset.groupId}, review ${index + 1}: name, content and a 1–5 rating are required.`); return;
          }
          const id = String(entry.id || hash(`${entry.name}|${entry.date || ''}|${entry.content}`));
          if (ids.has(id)) { warnings.push(`Duplicate review ID: ${id}.`); return; }
          ids.add(id);
          const date = /^\d{4}-\d{2}-\d{2}$/.test(entry.date || '') ? Date.parse(entry.date + 'T00:00:00Z') : NaN;
          this.reviews.push({ id, group: group.dataset.groupId, name: entry.name.trim().slice(0, 80), rating, content: entry.content.slice(0, 10000), date: Number.isFinite(date) ? date : 0, likes: count(entry.likes), dislikes: count(entry.dislikes), flag: group.dataset.flagUrl || '', country: group.dataset.country || 'Country flag' });
        });
      });
      const warning = this.querySelector('[data-config-status]');
      if (warning) { warning.hidden = !warnings.length; warning.textContent = warnings.join(' '); }
    }

    addSampleReviews() {
      const notice = this.querySelector('[data-sample-reviews]');
      const previewRequested = new URLSearchParams(window.location.search).get('sample_reviews') === '1';
      const editorEnabled = this.dataset.designMode === 'true' && notice?.dataset.editorEnabled === 'true';
      if (!notice || (!editorEnabled && !previewRequested) || this.reviews.length) return;
      const groups = [...this.querySelectorAll('[data-review-group]')];
      const lengths = [
        'Short sample text for checking the spacing of a review row.',
        'This is sample content for layout testing. It demonstrates how a medium-length review wraps beside the customer name, badge and rating.',
        'Sample review for preview purposes only. This longer paragraph helps check text wrapping on desktop, tablet and mobile screens. It is not a customer testimonial and does not describe a purchase.\nA second paragraph tests line spacing and the distance above the helpfulness buttons.'
      ];
      this.usingSamples = true;
      notice.hidden = false;
      this.reviews = Array.from({ length: 200 }, (_, index) => {
        const group = groups[Math.floor(index / 50)] || groups[0];
        return {
          id: `layout-sample-${index + 1}`, group: group?.dataset.groupId || 'sample',
          name: `Sample customer ${String(index + 1).padStart(3, '0')}`,
          rating: index % 10 < 6 ? 5 : index % 10 < 8 ? 4 : index % 10 === 8 ? 3 : index % 20 === 9 ? 2 : 1,
          content: lengths[index % lengths.length], date: Date.UTC(2026, 9, 14) - index * 86400000,
          likes: 0, dislikes: 0, flag: group?.dataset.flagUrl || '', country: group?.dataset.country || 'Sample flag'
        };
      });
    }

    renderSummary() {
      const total = this.reviews.length;
      const average = total ? this.reviews.reduce((sum, review) => sum + review.rating, 0) / total : 0;
      const sourceTotal = count(this.dataset.summaryTotal);
      const sourceScore = Number(this.dataset.summaryScore);
      const sourced = !this.usingSamples && this.dataset.summaryMode === 'source' && this.dataset.summarySource.trim() && sourceTotal >= total && sourceTotal > 0 && sourceScore >= 1 && sourceScore <= 5;
      const shownScore = sourced ? sourceScore : average;
      this.querySelector('[data-score]').textContent = shownScore.toFixed(1);
      this.querySelector('[data-total]').textContent = `${(sourced ? sourceTotal : total).toLocaleString('en-US')} reviews`;
      const overallStars = this.querySelector('[data-overall-stars]');
      overallStars.textContent = '★★★★★';
      overallStars.style.setProperty('--star-percent', `${shownScore * 20}%`);
      overallStars.setAttribute('aria-label', `${shownScore.toFixed(1)} out of 5 stars`);
      const note = this.querySelector('[data-summary-source-note]');
      note.hidden = !sourced;
      if (sourced) {
        note.textContent = `Overall rating from ${this.dataset.summarySource}.`;
        try {
          const url = new URL(this.dataset.summaryUrl, location.href);
          if (this.dataset.summaryUrl && ['http:', 'https:'].includes(url.protocol)) {
            const link = text('a', '', ' View source'); link.href = url.href; note.append(link);
          }
        } catch { /* Optional source URL. */ }
      }
      this.querySelector('[data-breakdown-label]').textContent = sourced ? `Breakdown of ${total.toLocaleString('en-US')} listed reviews` : 'Rating breakdown';
      this.querySelectorAll('[data-stars]').forEach(row => {
        const matches = this.reviews.filter(review => review.rating === Number(row.dataset.stars)).length;
        row.querySelector('.cr-bar > span').style.width = `${total ? matches / total * 100 : 0}%`;
        row.querySelector('.cr-bar-count').textContent = matches.toLocaleString('en-US');
        row.setAttribute('aria-label', `${row.dataset.stars} stars, ${matches} reviews. Filter reviews.`);
      });
    }

    voteKey(review) { return `customer-review-vote:v1:${this.dataset.product}:${review.id}`; }
    voteValue(review) {
      const key = this.voteKey(review);
      if (this.voteMemory.has(key)) return this.voteMemory.get(key);
      let value;
      try { value = localStorage.getItem(key); } catch { /* Private browsing. */ }
      return value === 'up' ? value : null;
    }
    filtered() {
      const rating = Number(this.filter.value);
      return this.reviews.filter(review => !rating || review.rating === rating).sort((a, b) => {
        if (this.sort.value === 'highest') return b.rating - a.rating || b.date - a.date;
        if (this.sort.value === 'lowest') return a.rating - b.rating || b.date - a.date;
        if (this.sort.value === 'helpful') return b.likes - a.likes || b.date - a.date;
        return b.date - a.date;
      });
    }

    render(append = false) {
      const filtered = this.filtered();
      const visible = Math.min(this.visibleCount, filtered.length);
      const start = append ? this.list.children.length : 0;
      const added = filtered.slice(start, visible).map(review => this.reviewNode(review));
      if (append) this.list.append(...added);
      else this.list.replaceChildren(...added);
      this.querySelector('[data-results]').textContent = filtered.length ? `Showing ${visible} of ${filtered.length.toLocaleString('en-US')} reviews` : '0 reviews';
      const empty = this.querySelector('[data-empty]'); empty.hidden = filtered.length > 0;
      empty.textContent = this.reviews.length ? 'No reviews match your filter. Try another rating.' : 'No reviews yet.';
      this.querySelector('[data-load-more]').hidden = visible >= filtered.length;
      this.querySelectorAll('[data-stars]').forEach(row => row.setAttribute('aria-pressed', String(row.dataset.stars === this.filter.value)));
    }

    reviewNode(review) {
      const node = text('article', 'cr-review'); node.dataset.reviewId = review.id;
      const person = text('div', 'cr-person');
      const avatar = text('span', 'cr-avatar', review.name.trim().split(/\s+/).filter(Boolean).filter((_, index, parts) => index === 0 || index === parts.length - 1).map(part => Array.from(part)[0]).join('').toLocaleUpperCase());
      const avatarColors = ['#e3edf8', '#e5efe5', '#f5e8d6', '#eee5f5', '#f5e2e5', '#deefed', '#ecebdc', '#e5e7f5'];
      const avatarSeed = Array.from(review.name.trim().toLocaleLowerCase()).reduce((hash, char) => (Math.imul(hash, 31) + char.codePointAt(0)) >>> 0, 0);
      avatar.style.backgroundColor = avatarColors[avatarSeed % avatarColors.length];
      person.append(avatar);
      const author = text('div', 'cr-name'); author.append(text('strong', '', review.name));
      const badge = text('span', 'cr-verified');
      badge.title = 'Verified Customer';
      
      
      const seal = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      seal.setAttribute('viewBox', '0 0 24 24'); seal.setAttribute('aria-hidden', 'true');
      const shape = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      shape.setAttribute('d', 'm12 1.5 2.7 1.8 3.2.2 1.2 3 2.5 2-.8 3.1.8 3.1-2.5 2-1.2 3-3.2.2-2.7 1.8-2.7-1.8-3.2-.2-1.2-3-2.5-2 .8-3.1-.8-3.1 2.5-2 1.2-3 3.2-.2Z');
      shape.setAttribute('fill', 'currentColor');
      const tick = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      tick.setAttribute('d', 'm7.5 12 3 3 6-6'); tick.setAttribute('fill', 'none'); tick.setAttribute('stroke', '#fff'); tick.setAttribute('stroke-width', '2'); tick.setAttribute('stroke-linecap', 'round'); tick.setAttribute('stroke-linejoin', 'round');
      seal.append(shape, tick);
      if (this.dataset.verifiedIcon) {
        const icon = text('img', 'cr-verified-icon'); icon.src = this.dataset.verifiedIcon; icon.alt = ''; icon.width = 18; icon.height = 18; icon.loading = 'lazy'; badge.append(icon);
      } else badge.append(seal);
      badge.append(text('span', '', 'Verified Customer'));
      if (review.flag) { const flag = text('img', 'cr-flag'); flag.src = review.flag; flag.alt = review.country; flag.width = 24; flag.height = 16; flag.loading = 'lazy'; author.append(flag); }
      person.append(author, badge);
      const content = text('div', 'cr-review-content');
      const meta = text('div', 'cr-review-meta'); meta.append(stars(review.rating));
      content.append(meta);
      content.append(text('p', 'cr-body', review.content));
      const votes = text('div', 'cr-votes');
      const current = this.voteValue(review);
      for (const [direction, label, baseline] of [['up', 'Like', review.likes]]) {
        const button = text('button', 'cr-vote'); button.type = 'button'; button.dataset.vote = direction;
        button.setAttribute('aria-label', `${label}: ${baseline + (current === direction ? 1 : 0)}`);
        button.setAttribute('aria-pressed', String(current === direction));
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '16'); svg.setAttribute('height', '16'); svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.5'); svg.setAttribute('aria-hidden', 'true');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path'); path.setAttribute('d', 'M7 10H3v11h4V10Zm0 0 5-8c3 0 2 4 1 6h6a2 2 0 0 1 2 2l-2 9a2 2 0 0 1-2 2H7'); svg.append(path);
        button.append(svg, text('span', '', baseline + (current === direction ? 1 : 0))); votes.append(button);
      }
      content.append(votes); node.append(person, content);
      return node;
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button'); if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-write')) {
        if (this.form.dataset.complete === 'true') {
          this.form.reset();
          delete this.form.dataset.complete;
          this.querySelector('[data-submit-status]').hidden = true;
        }
        this.opener = button; this.dialog.showModal();
      }
      if (button.hasAttribute('data-close')) this.dialog.close();
      if (button.hasAttribute('data-load-more')) {
        this.visibleCount += 10;
        this.render(true);
      }
      if (button.dataset.stars) { this.filter.value = this.filter.value === button.dataset.stars ? 'all' : button.dataset.stars; this.visibleCount = 10; this.render(); }
      if (button.dataset.vote === 'up') {
        const review = this.reviews.find(item => item.id === button.closest('[data-review-id]').dataset.reviewId); if (!review) return;
        const value = this.voteValue(review) === button.dataset.vote ? null : button.dataset.vote;
        const key = this.voteKey(review); this.voteMemory.set(key, value);
        try { if (value) localStorage.setItem(key, value); else localStorage.removeItem(key); } catch { /* Keep vote for this visit. */ }
        const replacement = this.reviewNode(review);
        button.closest('[data-review-id]').replaceWith(replacement);
        replacement.querySelector(`[data-vote="${button.dataset.vote}"]`).focus({ preventScroll: true });
        this.querySelector('[data-status]').textContent = value ? 'Your vote is saved on this device.' : 'Your vote has been removed.';
      }
    }
  });
})();
